import { useEffect, useState, useMemo } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowRight, Phone, Mail, CalendarDays } from "lucide-react";
import { useApi } from "@/lib/api";
import {
  cleanMarkdownBody,
  extractTitleAndSubtitle,
  ColorBlock,
  type ColorBlockTint,
} from "@/components/page";
import { cn } from "@/lib/utils";

type ClientType = "individual" | "couple" | "student" | "lead";
type ClientStatus = "active" | "paused" | "archived";

type ClientDetail = {
  id: string;
  name: string;
  type: ClientType;
  goal: string | null;
  status: ClientStatus;
  phone: string | null;
  email: string | null;
  notes: string | null;
  session_count: number;
  created_at: string;
  updated_at: string;
  sessions: {
    id: string;
    scheduled_at: string;
    summary: string | null;
    created_at: string;
  }[];
};

const TYPE_LABELS: Record<ClientType, string> = {
  individual: "פרטי",
  couple: "זוגי",
  student: "סטודנטית",
  lead: "ליד",
};

const STATUS_LABELS: Record<ClientStatus, string> = {
  active: "פעילה",
  paused: "מושהית",
  archived: "בארכיון",
};

const TYPE_TINT: Record<ClientType, ColorBlockTint> = {
  individual: "blue",
  couple: "lilac",
  student: "sage",
  lead: "peach",
};

const dateOnlyFmt = new Intl.DateTimeFormat("he-IL", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
});

const datePillFmt = new Intl.DateTimeFormat("he-IL", {
  day: "2-digit",
  month: "short",
});

type TabKey = "overview" | "sessions" | "details";

const TABS: { key: TabKey; label: string }[] = [
  { key: "overview", label: "סקירה" },
  { key: "sessions", label: "מפגשים" },
  { key: "details", label: "פרטים" },
];

export function ClientDetailPage() {
  const { id } = useParams<{ id: string }>();
  const api = useApi();
  const [client, setClient] = useState<ClientDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<TabKey>("overview");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!id) return;
      setLoading(true);
      setError(null);
      try {
        const res = await api(`/api/clients/${id}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = (await res.json()) as ClientDetail;
        if (!cancelled) setClient(data);
      } catch (e) {
        if (!cancelled)
          setError(e instanceof Error ? e.message : "שגיאה בטעינה");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (loading && !client) {
    return (
      <PageFrame>
        <p className="text-sm text-stone-500">טוענת את פרטי המתאמנת…</p>
      </PageFrame>
    );
  }

  if (error || !client) {
    return (
      <PageFrame>
        <h1 className="text-3xl font-bold tracking-tight text-stone-900">
          המתאמנת לא נמצאה
        </h1>
        <p className="mt-3 text-sm text-stone-600">
          {error ?? "לא נמצאה מתאמנת לפי המזהה."}
        </p>
        <Link
          to="/clients"
          className="mt-6 inline-flex items-center gap-2 text-sm text-stone-700 hover:text-stone-900"
        >
          <ArrowRight className="h-4 w-4" />
          חזרה לרשימת המתאמנות
        </Link>
      </PageFrame>
    );
  }

  const lastSession = client.sessions[0] ?? null;

  return (
    <div className="-mx-6 -my-8 px-6 py-6 sm:py-7 bg-stone-50 h-svh flex flex-col overflow-hidden">
      <div className="mx-auto w-full max-w-5xl flex-1 flex flex-col min-h-0 gap-5">
        {/* Back link */}
        <Link
          to="/clients"
          className="inline-flex items-center gap-2 text-sm text-stone-500 hover:text-stone-900 transition-colors w-fit"
        >
          <ArrowRight className="h-4 w-4" />
          חזרה לכל המתאמנות
        </Link>

        {/* Compact hero — no avatar */}
        <ColorBlock tint={TYPE_TINT[client.type]} size="md">
          <div className="grid sm:grid-cols-[1fr_auto] gap-6 items-center">
            <div className="min-w-0">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] opacity-65">
                {TYPE_LABELS[client.type]} · {STATUS_LABELS[client.status]}
              </p>
              <h1 className="mt-2 text-3xl sm:text-4xl font-bold tracking-[-0.03em] leading-[1.05]">
                {client.name}
              </h1>
            </div>

            <div className="flex gap-8 sm:gap-10">
              <Metric value={client.session_count} label="מפגשים" />
              <Metric
                value={
                  lastSession
                    ? dateOnlyFmt.format(new Date(lastSession.scheduled_at))
                    : "—"
                }
                label="מפגש אחרון"
                ltr
              />
            </div>
          </div>
        </ColorBlock>

        {/* Tab bar */}
        <div className="flex items-center gap-2 flex-wrap">
          {TABS.map((t) => {
            const active = t.key === tab;
            const count =
              t.key === "sessions" ? client.sessions.length : undefined;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                className={cn(
                  "rounded-full px-5 h-10 text-sm font-medium transition-colors",
                  "inline-flex items-center gap-2",
                  active
                    ? "bg-stone-900 text-stone-50"
                    : "bg-stone-200/70 text-stone-700 hover:bg-stone-200",
                )}
              >
                {t.label}
                {count !== undefined ? (
                  <span
                    className={cn(
                      "rounded-full px-2 py-0.5 text-[11px] tabular-nums",
                      active
                        ? "bg-stone-50/20 text-stone-50"
                        : "bg-stone-50/80 text-stone-700",
                    )}
                  >
                    {count}
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>

        {/* Tab content */}
        <div className="flex-1 min-h-0 overflow-hidden">
          {tab === "overview" && (
            <OverviewTab client={client} lastSession={lastSession} />
          )}
          {tab === "sessions" && <SessionsTab client={client} />}
          {tab === "details" && <DetailsTab client={client} />}
        </div>
      </div>
    </div>
  );
}

function PageFrame({ children }: { children: React.ReactNode }) {
  return (
    <div className="-mx-6 -my-8 px-6 py-8 bg-stone-50 h-svh">
      <div className="mx-auto max-w-5xl">{children}</div>
    </div>
  );
}

function Metric({
  value,
  label,
  ltr,
}: {
  value: React.ReactNode;
  label: string;
  ltr?: boolean;
}) {
  return (
    <div className="text-right">
      <p
        className="text-2xl sm:text-3xl font-bold tracking-tight tabular-nums leading-none"
        dir={ltr ? "ltr" : undefined}
      >
        {value}
      </p>
      <p className="mt-1.5 text-[11px] uppercase tracking-[0.18em] opacity-70">
        {label}
      </p>
    </div>
  );
}

/* ---------- Overview tab ---------- */

function OverviewTab({
  client,
  lastSession,
}: {
  client: ClientDetail;
  lastSession: ClientDetail["sessions"][number] | null;
}) {
  const lastBody = useMemo(
    () => (lastSession ? summaryShort(lastSession.summary) : ""),
    [lastSession],
  );

  return (
    <div className="h-full overflow-y-auto pe-1">
      <div className="grid sm:grid-cols-5 gap-5">
        <ColorBlock
          tint="cream"
          size="md"
          eyebrow="מטרת האימון"
          className="sm:col-span-3"
        >
          {client.goal ? (
            <p className="text-xl sm:text-2xl font-medium leading-snug tracking-tight text-stone-900">
              {client.goal}
            </p>
          ) : (
            <p className="text-base text-stone-700/85 italic">
              עוד לא הוגדרה מטרה למתאמנת הזו.
            </p>
          )}
        </ColorBlock>

        <ColorBlock
          tint="stone"
          size="md"
          eyebrow="מפגש אחרון"
          className="sm:col-span-2"
        >
          {lastSession ? (
            <div>
              <p
                className="text-base font-bold tabular-nums text-stone-900"
                dir="ltr"
              >
                {dateOnlyFmt.format(new Date(lastSession.scheduled_at))}
              </p>
              {lastBody ? (
                <p className="mt-3 text-sm text-stone-700 leading-relaxed line-clamp-5">
                  {lastBody}
                </p>
              ) : (
                <p className="mt-3 text-sm text-stone-500 italic">ללא סיכום.</p>
              )}
            </div>
          ) : (
            <p className="text-sm text-stone-700/85">עוד לא תועד מפגש.</p>
          )}
        </ColorBlock>
      </div>

      <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
        <InfoChip label="סוג" value={TYPE_LABELS[client.type]} />
        <InfoChip label="סטטוס" value={STATUS_LABELS[client.status]} />
        <InfoChip
          label="נפתחה"
          value={dateOnlyFmt.format(new Date(client.created_at))}
          ltr
        />
        <InfoChip
          label="עודכנה"
          value={dateOnlyFmt.format(new Date(client.updated_at))}
          ltr
        />
      </div>
    </div>
  );
}

function InfoChip({
  label,
  value,
  ltr,
}: {
  label: string;
  value: string;
  ltr?: boolean;
}) {
  return (
    <div className="rounded-2xl bg-white px-4 py-3">
      <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-stone-500">
        {label}
      </p>
      <p
        className="mt-1.5 text-sm font-semibold text-stone-900 tabular-nums"
        dir={ltr ? "ltr" : undefined}
      >
        {value}
      </p>
    </div>
  );
}

function summaryShort(raw: string | null): string {
  if (!raw) return "";
  const cleaned = cleanMarkdownBody(raw)
    .replace(/^#{1,6}\s+/gm, "")
    .replace(/^[>\-*]\s+/gm, "")
    .replace(/[*_`]+/g, "")
    .trim();
  return cleaned
    .split(/\n+/)
    .map((s) => s.trim())
    .filter((s) => s && !/^[-*_]{3,}$/.test(s))
    .slice(0, 4)
    .join(" · ");
}

/* ---------- Sessions tab — table → drill into SessionDetailPage ---------- */

type SessionItem = ClientDetail["sessions"][number];
type SessionWithMeta = SessionItem & { _title: string; _subtitle: string };

function SessionsTab({ client }: { client: ClientDetail }) {
  const navigate = useNavigate();
  const enriched: SessionWithMeta[] = useMemo(
    () =>
      client.sessions.map((s) => {
        const { title, subtitle } = extractTitleAndSubtitle(s.summary);
        return { ...s, _title: title, _subtitle: subtitle };
      }),
    [client.sessions],
  );

  if (enriched.length === 0) {
    return (
      <div className="h-full overflow-y-auto pe-1">
        <ColorBlock tint="stone" size="lg">
          <p className="text-base text-stone-700/85 leading-relaxed max-w-md">
            עוד לא נשמר מפגש למתאמנת הזו. אחרי המפגש הבא, הסיכום יופיע כאן.
          </p>
        </ColorBlock>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto pe-1">
      <div className="rounded-[24px] bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[11px] font-semibold uppercase tracking-[0.18em] text-stone-500">
              <th className="px-6 py-4 text-right font-semibold tabular-nums w-16">
                #
              </th>
              <th className="px-6 py-4 text-right font-semibold w-32">תאריך</th>
              <th className="px-6 py-4 text-right font-semibold">כותרת</th>
              <th className="px-6 py-4 text-right font-semibold">סיכום</th>
            </tr>
          </thead>
          <tbody>
            {enriched.map((s, idx) => {
              const number = enriched.length - idx;
              return (
                <tr
                  key={s.id}
                  onClick={() =>
                    navigate(`/clients/${client.id}/sessions/${s.id}`)
                  }
                  className={cn(
                    "border-t border-stone-200/70 cursor-pointer",
                    "hover:bg-stone-50 transition-colors",
                  )}
                >
                  <td className="px-6 py-4 align-middle font-bold tabular-nums text-stone-900">
                    {number}
                  </td>
                  <td
                    className="px-6 py-4 align-middle text-stone-700 tabular-nums"
                    dir="ltr"
                  >
                    {dateOnlyFmt.format(new Date(s.scheduled_at))}
                  </td>
                  <td className="px-6 py-4 align-middle">
                    <span className="text-base font-semibold text-stone-900 tracking-tight line-clamp-1">
                      {s._title ||
                        `מפגש מ־${datePillFmt.format(new Date(s.scheduled_at))}`}
                    </span>
                  </td>
                  <td className="px-6 py-4 align-middle text-stone-600 max-w-[28rem]">
                    <span className="line-clamp-1">{s._subtitle || "—"}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ---------- Details tab ---------- */

function DetailsTab({ client }: { client: ClientDetail }) {
  return (
    <div className="h-full overflow-y-auto pe-1">
      <div className="grid sm:grid-cols-5 gap-5">
        <ColorBlock
          tint="cream"
          size="md"
          eyebrow="יצירת קשר"
          className="sm:col-span-3"
        >
          <ul className="space-y-3">
            <ContactRow
              icon={Phone}
              label="טלפון"
              value={client.phone}
              href={client.phone ? `tel:${client.phone}` : null}
            />
            <ContactRow
              icon={Mail}
              label="אימייל"
              value={client.email}
              href={client.email ? `mailto:${client.email}` : null}
            />
          </ul>
        </ColorBlock>

        <ColorBlock
          tint="stone"
          size="md"
          eyebrow="מסלול"
          className="sm:col-span-2"
        >
          <ul className="space-y-3 text-sm">
            <MetaRow
              icon={CalendarDays}
              label="נפתחה"
              value={dateOnlyFmt.format(new Date(client.created_at))}
            />
            <MetaRow
              icon={CalendarDays}
              label="עודכנה"
              value={dateOnlyFmt.format(new Date(client.updated_at))}
            />
          </ul>
        </ColorBlock>

        {client.notes ? (
          <ColorBlock
            tint="stone"
            size="md"
            eyebrow="הערות"
            className="sm:col-span-5"
          >
            <p className="text-sm text-stone-700 leading-relaxed whitespace-pre-wrap max-w-2xl">
              {client.notes}
            </p>
          </ColorBlock>
        ) : null}
      </div>
    </div>
  );
}

function ContactRow({
  icon: Icon,
  label,
  value,
  href,
}: {
  icon: typeof Phone;
  label: string;
  value: string | null;
  href: string | null;
}) {
  return (
    <li className="flex items-center gap-3">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/70">
        <Icon className="h-4 w-4 text-stone-700" />
      </div>
      <div className="min-w-0">
        <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-stone-700/65">
          {label}
        </p>
        {value ? (
          href ? (
            <a
              href={href}
              className="text-sm font-medium text-stone-900 hover:underline tabular-nums"
              dir="ltr"
            >
              {value}
            </a>
          ) : (
            <p
              className="text-sm font-medium text-stone-900 tabular-nums"
              dir="ltr"
            >
              {value}
            </p>
          )
        ) : (
          <p className="text-sm text-stone-500 italic">—</p>
        )}
      </div>
    </li>
  );
}

function MetaRow({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof CalendarDays;
  label: string;
  value: string;
}) {
  return (
    <li className="flex items-center gap-3">
      <Icon className="h-4 w-4 shrink-0 text-stone-600" />
      <span className="text-[11px] uppercase tracking-[0.18em] text-stone-700/65 w-16">
        {label}
      </span>
      <span className="text-stone-900 font-medium tabular-nums" dir="ltr">
        {value}
      </span>
    </li>
  );
}
